// src/pages/NotificationAdminPage.js - 通知管理専用ページ
import React, { useState, useEffect } from "react";
import { NotificationService } from "../utils/notificationService";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { Send, Users, AlertTriangle, Calendar, FileText, Settings as SettingsIcon } from "lucide-react";

function NotificationAdminPage() {
  const [staffList, setStaffList] = useState([]);
  const [notification, setNotification] = useState({
    title: "",
    message: "",
    type: "system",
    priority: "normal",
    actionUrl: "",
    targetType: "all" // all, specific, role
  });
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchStaffList();
  }, []);

  const fetchStaffList = async () => {
    try {
      const staffSnapshot = await getDocs(collection(db, "staffList"));
      const staff = staffSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setStaffList(staff);
    } catch (error) {
      console.error("スタッフリスト取得エラー:", error);
    }
  };

  const handleSendNotification = async () => {
    if (!notification.title || !notification.message) {
      alert("タイトルとメッセージは必須です");
      return;
    }

    setIsLoading(true);

    try {
      let recipientIds = [];

      switch (notification.targetType) {
        case "all":
          await NotificationService.createGlobalNotification({
            title: notification.title,
            message: notification.message,
            type: notification.type,
            priority: notification.priority,
            actionUrl: notification.actionUrl || null,
          });
          break;

        case "specific":
          if (selectedStaff.length === 0) {
            alert("送信先スタッフを選択してください");
            return;
          }
          recipientIds = selectedStaff;
          await NotificationService.createBulkNotification({
            recipientIds,
            title: notification.title,
            message: notification.message,
            type: notification.type,
            priority: notification.priority,
            actionUrl: notification.actionUrl || null,
          });
          break;

        case "role":
          if (!selectedRole) {
            alert("対象の役職を選択してください");
            return;
          }
          await NotificationService.createConditionalNotification({
            condition: (staff) => staff.role === selectedRole,
            title: notification.title,
            message: notification.message,
            type: notification.type,
            priority: notification.priority,
            actionUrl: notification.actionUrl || null,
          });
          break;
      }

      alert("通知を送信しました");
      
      // フォームリセット
      setNotification({
        title: "",
        message: "",
        type: "system",
        priority: "normal",
        actionUrl: "",
        targetType: "all"
      });
      setSelectedStaff([]);
      setSelectedRole("");

    } catch (error) {
      console.error("通知送信エラー:", error);
      alert("通知の送信に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'shift': return <Calendar className="w-4 h-4" />;
      case 'assignment': return <Users className="w-4 h-4" />;
      case 'procedure': return <FileText className="w-4 h-4" />;
      case 'urgent': return <AlertTriangle className="w-4 h-4" />;
      default: return <SettingsIcon className="w-4 h-4" />;
    }
  };

  const quickNotifications = [
    {
      title: "シフト確認のお願い",
      message: "来月のシフト希望の提出期限が近づいています。未提出の方は至急提出してください。",
      type: "shift",
      priority: "high"
    },
    {
      title: "新しい感染対策手順",
      message: "新型コロナウイルス対策の手順が更新されました。手順書を確認してください。",
      type: "procedure",
      priority: "high"
    },
    {
      title: "システムメンテナンス",
      message: "明日22:00-24:00の間、システムメンテナンスを実施します。ご協力をお願いします。",
      type: "system",
      priority: "normal"
    },
    {
      title: "緊急連絡",
      message: "重要なお知らせがあります。至急確認してください。",
      type: "urgent",
      priority: "urgent"
    }
  ];

  return (
    <div style={{ 
      padding: "6rem 2rem 2rem 2rem", 
      maxWidth: "900px", 
      margin: "0 auto",
      fontFamily: "sans-serif"
    }}>
      {/* ページヘッダー */}
      <div style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "2rem",
        marginBottom: "2rem",
        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
        border: "1px solid #e0e0e0"
      }}>
        <h1 style={{ 
          fontSize: "2rem", 
          fontWeight: "bold", 
          marginBottom: "0.5rem", 
          display: "flex", 
          alignItems: "center", 
          gap: "0.75rem",
          color: "#333"
        }}>
          <div style={{
            padding: "0.5rem",
            backgroundColor: "#2196F3",
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}>
            <Send className="w-6 h-6" style={{ color: "white" }} />
          </div>
          通知管理
        </h1>
        <p style={{ color: "#666", margin: 0, fontSize: "1.1rem" }}>
          スタッフへの通知を送信・管理します
        </p>
      </div>

      {/* クイック通知 */}
      <div style={{ marginBottom: "2rem" }}>
        <h2 style={{ 
          fontSize: "1.3rem", 
          fontWeight: "600", 
          marginBottom: "1rem",
          color: "#333"
        }}>
          📱 クイック通知
        </h2>
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "1rem" 
        }}>
          {quickNotifications.map((quick, index) => (
            <div
              key={index}
              style={{
                padding: "1.25rem",
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                backgroundColor: "white",
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
              }}
              onClick={() => setNotification({ ...notification, ...quick })}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8f9fa";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 4px 15px rgba(0, 0, 0, 0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 5px rgba(0, 0, 0, 0.05)";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem" }}>
                <div style={{ color: "#2196F3" }}>
                  {getTypeIcon(quick.type)}
                </div>
                <strong style={{ fontSize: "1rem", color: "#333" }}>{quick.title}</strong>
                <span style={{ 
                  fontSize: "0.75rem", 
                  padding: "0.2rem 0.5rem", 
                  backgroundColor: quick.priority === 'urgent' ? '#ffebee' : quick.priority === 'high' ? '#fff3e0' : '#e3f2fd',
                  color: quick.priority === 'urgent' ? '#c62828' : quick.priority === 'high' ? '#f57c00' : '#1565c0',
                  borderRadius: "12px",
                  fontWeight: "600"
                }}>
                  {quick.priority === 'urgent' ? '🚨 緊急' : quick.priority === 'high' ? '⚡ 重要' : '📋 通常'}
                </span>
              </div>
              <div style={{ fontSize: "0.9rem", color: "#666", lineHeight: "1.4" }}>
                {quick.message}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 通知フォーム */}
      <div style={{
        padding: "2rem",
        border: "1px solid #e0e0e0",
        borderRadius: "16px",
        backgroundColor: "white",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)"
      }}>
        <h2 style={{ 
          fontSize: "1.3rem", 
          fontWeight: "600", 
          marginBottom: "1.5rem",
          color: "#333"
        }}>
          ✉️ カスタム通知作成
        </h2>

        <div style={{ display: "grid", gap: "1.5rem" }}>
          {/* タイトル */}
          <div>
            <label style={{ 
              display: "block", 
              fontWeight: "600", 
              marginBottom: "0.5rem",
              color: "#333"
            }}>
              タイトル <span style={{ color: "#f44336" }}>*</span>
            </label>
            <input
              type="text"
              value={notification.title}
              onChange={(e) => setNotification({ ...notification, title: e.target.value })}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                fontSize: "1rem",
                transition: "border-color 0.2s ease",
              }}
              placeholder="通知のタイトルを入力してください"
              onFocus={(e) => e.target.style.borderColor = "#2196F3"}
              onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
            />
          </div>

          {/* メッセージ */}
          <div>
            <label style={{ 
              display: "block", 
              fontWeight: "600", 
              marginBottom: "0.5rem",
              color: "#333"
            }}>
              メッセージ <span style={{ color: "#f44336" }}>*</span>
            </label>
            <textarea
              value={notification.message}
              onChange={(e) => setNotification({ ...notification, message: e.target.value })}
              rows={4}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                fontSize: "1rem",
                resize: "vertical",
                transition: "border-color 0.2s ease",
                lineHeight: "1.5"
              }}
              placeholder="通知の詳細内容を入力してください"
              onFocus={(e) => e.target.style.borderColor = "#2196F3"}
              onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
            />
          </div>

          {/* 設定行 */}
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
            gap: "1rem" 
          }}>
            {/* タイプ */}
            <div>
              <label style={{ 
                display: "block", 
                fontWeight: "600", 
                marginBottom: "0.5rem",
                color: "#333"
              }}>
                📂 種類
              </label>
              <select
                value={notification.type}
                onChange={(e) => setNotification({ ...notification, type: e.target.value })}
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  backgroundColor: "white"
                }}
              >
                <option value="system">🔧 システム</option>
                <option value="shift">📅 シフト</option>
                <option value="assignment">👥 配置</option>
                <option value="procedure">📋 手順書</option>
                <option value="urgent">🚨 緊急</option>
              </select>
            </div>

            {/* 優先度 */}
            <div>
              <label style={{ 
                display: "block", 
                fontWeight: "600", 
                marginBottom: "0.5rem",
                color: "#333"
              }}>
                ⭐ 優先度
              </label>
              <select
                value={notification.priority}
                onChange={(e) => setNotification({ ...notification, priority: e.target.value })}
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  backgroundColor: "white"
                }}
              >
                <option value="low">🔵 低</option>
                <option value="normal">⚪ 通常</option>
                <option value="high">🟡 高</option>
                <option value="urgent">🔴 緊急</option>
              </select>
            </div>

            {/* 送信先タイプ */}
            <div>
              <label style={{ 
                display: "block", 
                fontWeight: "600", 
                marginBottom: "0.5rem",
                color: "#333"
              }}>
                🎯 送信先
              </label>
              <select
                value={notification.targetType}
                onChange={(e) => setNotification({ ...notification, targetType: e.target.value })}
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  backgroundColor: "white"
                }}
              >
                <option value="all">👥 全員</option>
                <option value="specific">👤 指定</option>
                <option value="role">🏷️ 役職別</option>
              </select>
            </div>
          </div>

          {/* アクションURL */}
          <div>
            <label style={{ 
              display: "block", 
              fontWeight: "600", 
              marginBottom: "0.5rem",
              color: "#333"
            }}>
              🔗 リンクURL（オプション）
            </label>
            <input
              type="text"
              value={notification.actionUrl}
              onChange={(e) => setNotification({ ...notification, actionUrl: e.target.value })}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                fontSize: "1rem",
                transition: "border-color 0.2s ease",
              }}
              placeholder="例: /calendar, /procedures"
              onFocus={(e) => e.target.style.borderColor = "#2196F3"}
              onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
            />
          </div>

          {/* 送信先詳細設定 */}
          {notification.targetType === "specific" && (
            <div>
              <label style={{ 
                display: "block", 
                fontWeight: "600", 
                marginBottom: "0.5rem",
                color: "#333"
              }}>
                👤 送信先スタッフ選択
              </label>
              <div style={{
                maxHeight: "200px",
                overflowY: "auto",
                border: "2px solid #e0e0e0",
                borderRadius: "8px",
                padding: "0.5rem",
                backgroundColor: "#fafafa"
              }}>
                {staffList.map((staff) => (
                  <label
                    key={staff.employeeId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.5rem",
                      cursor: "pointer",
                      borderRadius: "4px",
                      transition: "background-color 0.2s ease",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f0f0"}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStaff.includes(staff.employeeId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedStaff([...selectedStaff, staff.employeeId]);
                        } else {
                          setSelectedStaff(selectedStaff.filter(id => id !== staff.employeeId));
                        }
                      }}
                      style={{ marginRight: "0.75rem", transform: "scale(1.2)" }}
                    />
                    <span style={{ fontWeight: "500" }}>
                      {staff.lastName} {staff.firstName} 
                      <span style={{ color: "#666", marginLeft: "0.5rem" }}>
                        ({staff.employeeId})
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {notification.targetType === "role" && (
            <div>
              <label style={{ 
                display: "block", 
                fontWeight: "600", 
                marginBottom: "0.5rem",
                color: "#333"
              }}>
                🏷️ 対象役職
              </label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.875rem",
                  border: "2px solid #e0e0e0",
                  borderRadius: "8px",
                  fontSize: "1rem",
                  backgroundColor: "white"
                }}
              >
                <option value="">選択してください</option>
                <option value="admin">🔑 管理者</option>
                <option value="shift_manager">📅 シフト管理者</option>
                <option value="assignment_manager">👥 配置管理者</option>
                <option value="staff">👤 スタッフ</option>
              </select>
            </div>
          )}

          {/* 送信ボタン */}
          <button
            onClick={handleSendNotification}
            disabled={isLoading}
            style={{
              padding: "1.25rem 2rem",
              backgroundColor: isLoading ? "#ccc" : "#2196F3",
              color: "white",
              border: "none",
              borderRadius: "12px",
              fontSize: "1.1rem",
              fontWeight: "600",
              cursor: isLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.75rem",
              transition: "all 0.2s ease",
              boxShadow: isLoading ? "none" : "0 4px 15px rgba(33, 150, 243, 0.3)",
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = "#1976D2";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 6px 20px rgba(33, 150, 243, 0.4)";
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = "#2196F3";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 15px rgba(33, 150, 243, 0.3)";
              }
            }}
          >
            <Send className="w-5 h-5" />
            {isLoading ? "送信中..." : "📤 通知を送信"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default NotificationAdminPage;